import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { CloseIcon } from '@wf-sl/common-icons'

import styles from './common.module.css'
import specificStyles from './form-iterator.module.css'

/**
 * @typedef {Object} FormIteratorItemProperties
 * @property {function(Object[], index): boolean}   onRemove            the method invoked before removing
 * @property {number}                               [partialFormIndex]  the index of the child
 * @property {Function}                             [bindTo]            the Form bind function
 * @property {Object}                               [error]             the Form error object
 * @property {Object}                               [model]             the model of the overlaying Form
 * @property {Function}                             [onModelChange]     the Form update function
 * @property {boolean}                              [canClose=false]    to allows the removal of the last item
 * @property {boolean}                              [disabled=false]    to disable the children
 */

// Can't set the "partialFormIndex" as required, because the "Form" renders without "partial" and
// therefore it will be "undefined". Maybe it's related to the root form thing in "form.js".

const itemPropTypes = {
    bindTo: PropTypes.func,
    canClose: PropTypes.bool,
    disabled: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    model: PropTypes.object,
    onModelChange: PropTypes.func,
    onRemove: PropTypes.func.isRequired,
    partialFormIndex: PropTypes.number
}

/**
 * This component wraps the children of the FormIterator and adds a close icon to each of it.
 * @constructor
 *
 * <b>Hint:</b>
 * The "model" is <code>undefined</code>, if the global "model" has no field with the specified "property" name.
 * (<code>model = globalModel[property] // undefined</code>)
 *
 * @param {FormIteratorItemProperties} props
 * @returns {React.ReactElement}
 */
export default function FormIteratorItem(props) {
    const { bindTo, error, model, onModelChange, partialFormIndex } = props
    const { canClose, children, disabled, onRemove } = props
    const composedClassName = cx(
        styles['iterator-item'],
        { [specificStyles['first-child']]: !partialFormIndex },
        't3sel-form-iterator-item',
        `t3sel-${partialFormIndex}`
    )

    return (
        <div className={composedClassName}>
            {React.Children.map(children, child =>
                React.cloneElement(child, {
                    bindTo,
                    disabled,
                    error,
                    index: partialFormIndex,
                    model,
                    onModelChange
                })
            )}
            {canClose && (
                <CloseIcon
                    className={cx(styles['close-icon'], { [styles['close-icon-disabled']]: disabled })}
                    onClick={() => (disabled ? {} : onRemove(partialFormIndex))}
                />
            )}
        </div>
    )
}

FormIteratorItem.propTypes = itemPropTypes
